import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { apiGet } from '../utils/api';
import { prepareBookingAccount } from '../utils/bookingAccountStorage';

const UserContext = createContext();
const ACCOUNT_EVENT_KEY = 'wowcruise:accountChanged';

export const UserProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [bookingStorageReady, setBookingStorageReady] = useState(false);
  const [bookingAccountEpoch, setBookingAccountEpoch] = useState(0);
  const [error, setError] = useState(null);
  const userRef = useRef(null);
  const requestVersion = useRef(0);

  const applyUser = useCallback((value) => {
    const next = typeof value === 'function' ? value(userRef.current) : value;
    try {
      if (prepareBookingAccount(next?.idx)) setBookingAccountEpoch(epoch => epoch + 1);
      setBookingStorageReady(true);
    } catch (storageError) {
      // Do not restore another account's booking if storage could not be isolated.
      setBookingStorageReady(false);
      setError(storageError);
    }
    userRef.current = next;
    setUserState(next);
  }, []);

  const setUser = useCallback((value) => {
    requestVersion.current += 1; // Ignore pre-logout session responses.
    applyUser(value);
    try { localStorage.setItem(ACCOUNT_EVENT_KEY, `${Date.now()}:${Math.random()}`); } catch (_) {}
  }, [applyUser]);

  const fetchSession = useCallback(async () => {
    const version = ++requestVersion.current;
    try {
      const res = await apiGet('/member/getSession.php');
      if (version !== requestVersion.current) return;
      applyUser(res.result === 'success' ? res.data : null);
      setError(null);
    } catch (err) {
      if (version !== requestVersion.current) return;
      // A temporary network failure must not erase the member's in-progress form.
      if (err?.response?.status === 401) applyUser(null);
      setError(err);
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
        setSessionReady(true);
      }
    }
  }, [applyUser]);

  useEffect(() => {
    fetchSession();
    const onStorage = event => { if (event.key === ACCOUNT_EVENT_KEY) fetchSession(); };
    const onFocus = () => { fetchSession(); };
    const onUnauthorized = () => { setUser(null); setLoading(false); setSessionReady(true); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    window.addEventListener('account:unauthorized', onUnauthorized);
    return () => {
      requestVersion.current += 1;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('account:unauthorized', onUnauthorized);
    };
  }, [fetchSession, setUser]);

  return (
    <UserContext.Provider value={{ user, setUser, fetchSession, loading, error, sessionReady, bookingStorageReady, bookingAccountEpoch }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
